"use client";
import { ChangeEvent, useActionState, useState } from "react";
import {
  submitConsultationForm,
  type ConsultationFormState,
} from "@/features/consultation-form";
import {
  formatPhoneMask,
  getPhoneValidationError,
} from "@/shared/lib/phone/phone";
import { Input } from "@/shared/ui/input/Input";
import { Button } from "@/shared/ui/button/Button";
import styles from "./ConsultationForm.module.css";
import { FormTurnstile } from "./FormTurnstile";

const initialState: ConsultationFormState = {
  success: false,
  errors: {},
};

const initialFormState = {
  phone: "",
  consent: false,
  marketing: false,
};

export default function ConsultationForm() {
  const [state, action, pending] = useActionState<
    ConsultationFormState,
    FormData
  >(
    submitConsultationForm,
    initialState,
  );

  const [formValues, setFormValues] = useState(initialFormState);
  const [phoneError, setPhoneError] = useState<string | undefined>();
  const [dismissedResetId, setDismissedResetId] = useState<number | undefined>();
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    const nextValue =
      name === "phone" && type !== "checkbox" ? formatPhoneMask(value) : value;

    if (name === "phone") {
      setPhoneError(undefined);
    }

    setFormValues((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : nextValue,
    }));
  };

  const handlePhoneBlur = () => {
    setPhoneError(getPhoneValidationError(formValues.phone));
  };

  const displayedPhoneError = state.errors.phone?.[0] ?? phoneError;

  const handleSendAgain = () => {
    setDismissedResetId(state.resetId);
    setFormValues(initialFormState);
    setPhoneError(undefined);
    setTurnstileToken(null);
  };

  const showSuccessScreen =
    state.success &&
    state.resetId !== undefined &&
    state.resetId !== dismissedResetId;

  if (showSuccessScreen) {
    return (
      <div className={styles.success} role="status" aria-live="polite">
        <p className={styles.successTitle}>Успешно</p>
        <Button
          type="button"
          variant="outline"
          className={styles.successButton}
          onClick={handleSendAgain}
        >
          Отправить ещё
        </Button>
      </div>
    );
  }

  return (
    <form key={state.resetId} action={action} className={styles.form}>
      <div>
        <Input
          name="phone"
          placeholder="+7 (999) 123-45-67"
          autoComplete="tel"
          inputMode="tel"
          value={formValues.phone}
          onChange={handleChange}
          onBlur={handlePhoneBlur}
          type="tel"
          maxLength={18}
          isError={!!displayedPhoneError}
        />
        {displayedPhoneError && (
          <p className={styles.errorMessage}>{displayedPhoneError}</p>
        )}
      </div>

      <div>
        <label className={styles.checkboxField}>
          <input
            type="checkbox"
            name="consent"
            checked={formValues.consent}
            onChange={handleChange}
          />
          Согласен на обработку персональных данных
        </label>
        {state.errors.consent && (
          <p className={styles.errorMessage}>{state.errors.consent[0]}</p>
        )}
      </div>

      <label className={styles.checkboxField}>
        <input
          type="checkbox"
          name="marketing"
          checked={formValues.marketing}
          onChange={handleChange}
        />
        Согласен на получение рекламных материалов
      </label>

      <FormTurnstile
        resetKey={state.turnstileResetId ?? "initial"}
        error={state.errors.turnstile?.[0]}
        onTokenChange={setTurnstileToken}
      />

      {state.errors.form && (
        <p className={styles.errorMessage}>{state.errors.form[0]}</p>
      )}

      <Button
        disabled={pending || !turnstileToken}
        type="submit"
        className={styles.button}
      >
        {pending ? "Отправка" : "Получить консультацию"}
      </Button>
    </form>
  );
}
